import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import AvidaLogo from "../components/brand/AvidaLogo"; // eslint-disable-line no-unused-vars
import {
  Mail, Lock, Loader2, Eye, EyeOff, ShieldCheck, Clock,
  Users, ClipboardList, FileCheck2, Stethoscope, CalendarDays,
  Receipt, GraduationCap, BadgeCheck, Settings2, HeartPulse,
  MessageSquare, Phone, Smartphone, Video, Voicemail, BookUser,
  ArrowRight, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { isTeamDomain } from "../lib/domainMode";

// Avida brand palette
const NAVY = "#00346f";
const NAVY_DEEP = "#021A4A";
const NAVY_MID = "#0A3B8F";
const GOLD = "#FFDA00";
const GOLD_DEEP = "#F4A500";

const HUB_MODULES = [
  { icon: Users,          label: "Clients & Census",    hint: "DHS 105.17 roster" },
  { icon: ClipboardList,  label: "Intake & Onboarding", hint: "DOC-101 → DOC-106" },
  { icon: FileCheck2,     label: "Authorizations",      hint: "PA tracker + wizard" },
  { icon: Stethoscope,    label: "Clinical",            hint: "PCST · Care plans" },
  { icon: HeartPulse,     label: "Supervisory Visits",  hint: "50-60 day DHS 107" },
  { icon: CalendarDays,   label: "Scheduling & EVV",    hint: "Sandata · Routes" },
  { icon: Receipt,        label: "Billing & Claims",    hint: "Pre-audit validator" },
  { icon: BadgeCheck,     label: "Compliance & Audit",  hint: "DHS 106.02(9)(f)" },
  { icon: GraduationCap,  label: "Training Library",    hint: "HIPAA · Annual renewals" },
  { icon: Settings2,      label: "Admin & Roles",       hint: "Users · Locations" },
];

const TEAM_MODULES = [
  { icon: MessageSquare,  label: "Team Chat",         hint: "Channels · DMs · Files" },
  { icon: Phone,          label: "Voice Calls",       hint: "SIP · Twilio · Recording" },
  { icon: Smartphone,     label: "SMS",               hint: "2-way texting" },
  { icon: Video,          label: "Video Meetings",    hint: "HIPAA-compliant rooms" },
  { icon: Voicemail,      label: "Voicemail",         hint: "Transcribed inbox" },
  { icon: BookUser,       label: "Shared Contacts",   hint: "Auto-synced directory" },
  { icon: CalendarDays,   label: "Calendar",          hint: "Team scheduling" },
  { icon: ShieldCheck,    label: "Secure by Default", hint: "End-to-end encrypted" },
];

const BRAND = {
  team: {
    name: "Team Avida",
    tagline: "Communications Hub",
    description:
      "One secure workspace for every conversation — voice, video, SMS, chat, and fax — built for healthcare teams.",
    modules: TEAM_MODULES,
    accent: GOLD,
  },
  hub: {
    name: "Avida Care",
    tagline: "Healthcare Operations Platform",
    description:
      "HIPAA-grade PCW agency management — patients, authorizations, EVV, billing, and compliance in one place.",
    modules: HUB_MODULES,
    accent: GOLD,
  },
};

function getDefaultRoute(role) {
  if (role === "pcw") return "/healthcare/pcw-portal";
  const hostname = window.location.hostname;
  if (hostname.startsWith("app.")) return "/healthcare";
  return "/dashboard";
}

/** Brand showcase panel — logo, tagline, and module grid */
function BrandPanel({ brand }) {
  return (
    <div
      className="hidden lg:flex lg:w-[56%] relative overflow-hidden flex-col"
      style={{
        background: `radial-gradient(circle at 20% 10%, ${NAVY_MID} 0%, ${NAVY} 45%, ${NAVY_DEEP} 100%)`,
      }}
      data-testid="login-brand-panel"
    >
      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            `linear-gradient(${GOLD}40 1px, transparent 1px), linear-gradient(90deg, ${GOLD}40 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
        }}
      />
      {/* Aurora sweep */}
      <div className="av-aurora absolute inset-0 pointer-events-none" aria-hidden />

      {/* Giant brand watermark — full-color Avida mark behind the content */}
      <div
        className="absolute inset-0 pointer-events-none flex items-center justify-end overflow-hidden"
        aria-hidden
      >
        <img
          src="/avida-logo-fullcolor.png"
          alt=""
          className="av-watermark select-none"
          draggable={false}
          style={{
            width: "min(78%, 880px)",
            transform: "translate(18%, 4%)",
            opacity: 0.18,
            filter: "drop-shadow(0 30px 60px rgba(0,0,0,0.35))",
          }}
        />
      </div>

      {/* Glow */}
      <div
        className="absolute -bottom-48 -left-24 w-[30rem] h-[30rem] rounded-full blur-3xl opacity-20 av-glow-2"
        style={{ background: NAVY_MID }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-12 xl:px-16 py-12">
        {/* Logo + wordmark */}
        <div className="flex items-center gap-4 mb-10">
          {/* Full-color Avida house-and-leaf mark on a white tile */}
          <div className="relative shrink-0" data-testid="login-logo">
            <div className="w-28 h-28 rounded-2xl bg-white flex items-center justify-center shadow-xl ring-1 ring-white/20 av-tile p-2">
              <img src="/avida-logo-mark.png" alt="Avida" className="w-full h-full object-contain" draggable={false} />
            </div>
          </div>
          <div className="leading-tight">
            <div
              className="text-2xl font-black tracking-tight text-white"
              style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}
              data-testid="login-app-name"
            >
              {brand.name}
            </div>
            <div className="text-[11px] font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
              {brand.tagline}
            </div>
          </div>
        </div>

        {/* Hero copy */}
        <div className="max-w-xl mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5 text-[10px] font-bold tracking-[0.2em] uppercase bg-white/5 border border-white/10 text-white/70">
            <Sparkles className="w-3 h-3" style={{ color: GOLD }} />
            HIPAA-grade · End-to-end encrypted
          </div>
          <h1
            className="text-4xl xl:text-[2.75rem] font-black leading-[1.15] text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}
            data-testid="login-hero-title"
          >
            Everything your agency needs,<br />
            <span
              className="inline-block relative pb-1"
              style={{
                background: `linear-gradient(90deg, ${GOLD} 0%, #FFE94A 50%, ${GOLD} 100%)`,
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                animation: "avShimmer 5s linear infinite",
                lineHeight: "1.2",
              }}
            >
              nothing it doesn&apos;t.
            </span>
          </h1>
          <p className="text-[15px] leading-relaxed text-white/70 max-w-lg" data-testid="login-app-desc">
            {brand.description}
          </p>
        </div>

        {/* Module showcase */}
        <div className="mb-auto">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-4" style={{ color: GOLD }}>
            What&apos;s inside
          </div>
          <div className="grid grid-cols-2 gap-2.5 max-w-2xl" data-testid="login-modules-grid">
            {brand.modules.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={m.label}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] hover:border-white/20 transition-all"
                  style={{
                    animation: `fadeInUp 0.5s ease-out ${0.05 * i}s both`,
                  }}
                  data-testid={`module-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                    style={{
                      background: `linear-gradient(135deg, ${GOLD}22 0%, ${GOLD}08 100%)`,
                      border: `1px solid ${GOLD}33`,
                    }}
                  >
                    <Icon className="w-4 h-4" style={{ color: GOLD }} strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-white leading-tight truncate">
                      {m.label}
                    </div>
                    <div className="text-[10.5px] text-white/50 truncate">{m.hint}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Compliance footer */}
        <div className="mt-10 pt-6 border-t border-white/10 flex items-center justify-between text-[11px] text-white/50">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: GOLD }} />
            PHI encrypted in transit &amp; at rest
          </div>
          <div className="font-mono">v2026.04</div>
        </div>
      </div>

      {/* Local keyframes */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes avHaloSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes avHaloPulse {
          0%, 100% { opacity: 0.55; filter: blur(6px); }
          50%      { opacity: 0.95; filter: blur(3px); }
        }
        .av-halo {
          animation:
            avHaloSpin 6s linear infinite,
            avHaloPulse 3.2s ease-in-out infinite;
        }
        @keyframes avTilePop {
          from { transform: scale(0.85); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }
        @keyframes avTileFloat {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-3px); }
        }
        .av-tile {
          animation:
            avTilePop 0.6s ease-out 0.1s backwards,
            avTileFloat 4s ease-in-out 1s infinite;
        }
        @keyframes avGlow1 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.25; }
          50%      { transform: translate(-24px, 20px) scale(1.08); opacity: 0.4; }
        }
        @keyframes avGlow2 {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.18; }
          50%      { transform: translate(30px, -18px) scale(1.12); opacity: 0.3; }
        }
        .av-glow-1 { animation: avGlow1 9s ease-in-out infinite; }
        .av-glow-2 { animation: avGlow2 11s ease-in-out infinite; }
        @keyframes avAurora {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .av-aurora {
          background:
            radial-gradient(900px 500px at 10% 90%, rgba(10,77,168,0.22), transparent 55%),
            radial-gradient(800px 500px at 90% 15%, rgba(10,77,168,0.18), transparent 55%),
            radial-gradient(700px 400px at 50% 50%, rgba(255,255,255,0.05), transparent 60%);
          background-size: 200% 200%;
          animation: avAurora 14s ease-in-out infinite;
          mix-blend-mode: screen;
        }
        @keyframes avShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .av-shimmer {
          background: linear-gradient(90deg, transparent 0%, rgba(255,218,0,0.25) 50%, transparent 100%);
          background-size: 200% 100%;
          animation: avShimmer 4s linear infinite;
        }
        @keyframes avWatermarkBreathe {
          0%, 100% { transform: translate(18%, 4%) scale(1); opacity: 0.18; }
          50%      { transform: translate(18%, 4%) scale(1.03); opacity: 0.24; }
        }
        .av-watermark {
          animation: avWatermarkBreathe 12s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .av-aurora, .av-glow-1, .av-glow-2, .av-halo, .av-shimmer, .av-tile, .av-watermark { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

function RecoveryCodesPanel({ codes, onContinue, navy, gold, continueLabel = "I've saved my codes — continue" }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  };
  return (
    <div data-testid="recovery-codes-panel">
      <div className="mb-4">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase mb-2" style={{ color: gold }}>
          <ShieldCheck className="w-4 h-4" /> Save your recovery codes
        </div>
        <h2 className="text-2xl font-black" style={{ color: navy, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}>
          Backup recovery codes
        </h2>
        <p className="text-slate-500 mt-2 text-sm">
          Store these somewhere safe. Each code works <strong>once</strong> and lets you sign in if you lose your authenticator. This is the only time they&apos;ll be shown.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="recovery-codes-list">
        {codes.map((c) => (
          <code key={c} className="text-sm font-mono text-slate-800 tracking-wide">{c}</code>
        ))}
      </div>
      <button
        type="button"
        onClick={copy}
        className="w-full h-11 rounded-xl text-xs font-bold border border-slate-200 text-slate-700 hover:bg-slate-50 mb-3"
        data-testid="recovery-codes-copy-btn"
      >
        {copied ? "Copied!" : "Copy all codes"}
      </button>
      <button
        type="button"
        onClick={onContinue}
        className="w-full h-12 rounded-xl text-sm font-bold text-white"
        style={{ background: navy }}
        data-testid="recovery-codes-continue-btn"
      >
        {continueLabel}
      </button>
    </div>
  );
}

function MfaEnrollment({ tempToken, email, onEnrolled, onCancel, navy, gold }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(true);
  const [err, setErr] = useState("");
  const [recovery, setRecovery] = useState(null);
  const pendingAuth = useRef(null);

  // Safari/iOS-safe request helper. We read text first and JSON-parse inside a
  // try so a non-JSON/empty/HTML error response (or a WebKit quirk) can never
  // bubble a raw DOMException like "The string did not match the expected
  // pattern." into the UI. Enrollment uses the Bearer token, not the cookie.
  const enrollFetch = useCallback(async (path, init = {}) => {
    const token = (tempToken || "").trim();
    if (!token || /[^\x21-\x7e]/.test(token)) {
      throw new Error("Your sign-in session expired. Please go back and sign in again.");
    }
    let res;
    try {
      res = await fetch(`${API}${path}`, {
        ...init,
        credentials: "omit",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(init.headers || {}),
        },
      });
    } catch {
      throw new Error("We couldn't reach the server. Check your connection and try again.");
    }
    const text = await res.text().catch(() => "");
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = null; }
    if (!res.ok) {
      throw new Error((data && data.detail) || `Request failed (${res.status}).`);
    }
    if (!data) throw new Error("The server returned an unexpected response. Please try again.");
    return data;
  }, [API, tempToken]);

  const loadSetup = useCallback(async () => {
    setStarting(true); setErr("");
    try {
      const d = await enrollFetch("/api/auth/2fa/setup", { method: "POST", body: "{}" });
      setSetup(d);
    } catch (e) {
      setErr(e.message || "We couldn't load your authenticator setup. Please try again.");
    } finally {
      setStarting(false);
    }
  }, [enrollFetch]);

  useEffect(() => { loadSetup(); }, [loadSetup]);

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setLoading(true); setErr("");
    try {
      const d = await enrollFetch("/api/auth/2fa/verify-setup", {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      pendingAuth.current = { token: d.access_token, user: d.user };
      if (d.recovery_codes && d.recovery_codes.length) {
        setRecovery(d.recovery_codes);
      } else {
        onEnrolled(d.access_token, d.user);
      }
    } catch (e2) {
      setErr(e2.message || "That code wasn't accepted. Check your phone's time and try again.");
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  if (recovery) {
    return (
      <RecoveryCodesPanel
        codes={recovery}
        navy={navy}
        gold={gold}
        onContinue={() => onEnrolled(pendingAuth.current.token, pendingAuth.current.user)}
      />
    );
  }

  return (
    <div data-testid="mfa-enrollment-gate">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase mb-2" style={{ color: gold }}>
          <ShieldCheck className="w-4 h-4" /> Security setup required
        </div>
        <h2 className="text-2xl font-black" style={{ color: navy, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}>
          Set up two-factor authentication
        </h2>
        <p className="text-slate-500 mt-2 text-sm">
          Avida now requires 2FA to protect client health information (HIPAA). Scan the code with an authenticator app{email ? ` for ${email}` : ""}, then enter the 6-digit code.
        </p>
      </div>

      {starting ? (
        <div className="py-10 text-center text-sm text-slate-500" data-testid="mfa-enroll-loading">Preparing your secure code…</div>
      ) : setup ? (
        <form onSubmit={verify} className="space-y-5" data-testid="mfa-enroll-form">
          {setup.qr_code && (
            <div className="flex justify-center">
              <img src={setup.qr_code} alt="2FA QR code" className="w-44 h-44 rounded-xl border border-slate-200" data-testid="mfa-enroll-qr" />
            </div>
          )}
          {setup.secret && (
            <div className="text-center">
              <p className="text-[11px] text-slate-500 mb-1">Or enter this key manually:</p>
              <code className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 font-mono text-slate-700 break-all">{setup.secret}</code>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-slate-700 text-xs font-bold tracking-wide uppercase">6-digit code</label>
            <input
              inputMode="numeric"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="w-full h-12 text-center text-2xl font-mono tracking-[0.4em] rounded-xl border border-slate-200 focus:outline-none focus:ring-2"
              style={{ letterSpacing: "0.4em" }}
              placeholder="000000"
              data-testid="mfa-enroll-code-input"
            />
          </div>
          {err && <p className="text-sm text-red-600" data-testid="mfa-enroll-error">{err}</p>}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full h-12 rounded-xl text-sm font-bold text-white disabled:opacity-50"
            style={{ background: navy }}
            data-testid="mfa-enroll-verify-btn"
          >
            {loading ? "Verifying…" : "Verify & Continue"}
          </button>
          <button type="button" onClick={onCancel} className="w-full text-xs text-slate-500 hover:text-slate-700" data-testid="mfa-enroll-cancel-btn">
            Back to sign in
          </button>
        </form>
      ) : (
        <div className="py-6 text-center">
          <p className="text-sm text-red-600 mb-4" data-testid="mfa-enroll-error">{err || "Something went wrong."}</p>
          <button
            onClick={loadSetup}
            disabled={starting}
            className="w-full h-11 rounded-xl text-sm font-bold text-white disabled:opacity-50 mb-3"
            style={{ background: navy }}
            data-testid="mfa-enroll-retry-btn"
          >
            {starting ? "Trying again…" : "Try again"}
          </button>
          <button onClick={onCancel} className="text-sm font-semibold" style={{ color: navy }} data-testid="mfa-enroll-cancel-btn">Back to sign in</button>
        </div>
      )}
    </div>
  );
}

export default function Login() {
  const { login, verify2FALogin, verify2FARecovery, applyAuth, isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const totpInputRef = useRef(null);
  // HIPAA MFA enforcement — forced enrollment gate (grace expired).
  const [enrollMode, setEnrollMode] = useState(false);
  const [enrollToken, setEnrollToken] = useState(null);

  const isTeam = isTeamDomain();
  const brand = isTeam ? BRAND.team : BRAND.hub;

  useEffect(() => {
    const reason = searchParams.get("reason");
    if (reason !== "timeout" && reason !== "session_ended" && reason !== "security_logout") return;
    // Defer one tick so the <Toaster/> is mounted before we fire — otherwise
    // the info toast is dropped on a cold redirect-to-login navigation.
    const t = setTimeout(() => {
      if (reason === "timeout") {
        toast.info("Your session has expired. Please sign in again.");
      } else if (reason === "security_logout") {
        toast.info("You were signed out for a security update. Please log in again.");
      } else {
        toast.info("You were signed out. This can happen if your account was signed in on another device or your session ended. Please sign in again.");
      }
    }, 150);
    return () => clearTimeout(t);
  }, [searchParams]);

  useEffect(() => {
    if (requires2FA && totpInputRef.current) totpInputRef.current.focus();
  }, [requires2FA]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    const rt = searchParams.get("returnTo");
    return <Navigate to={rt || getDefaultRoute(user?.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(formData.email, formData.password);
      if (result.requires_2fa) {
        setRequires2FA(true);
        setTempToken(result.temp_token);
        setLoading(false);
        return;
      }
      // HIPAA MFA enforcement: grace expired → must enroll before entering.
      if (result.requires_mfa_enrollment) {
        setEnrollToken(result.temp_token);
        setEnrollMode(true);
        setLoading(false);
        return;
      }
      toast.success("Welcome back!");
      // Grace nag — still allowed in, but prompt to set up 2FA.
      if (result.mfa_enrollment_required) {
        const d = result.mfa_grace_days_remaining;
        toast.warning(
          `Two-factor authentication will be required in ${d} day${d === 1 ? "" : "s"}. Set it up now in Security Settings.`,
          { duration: 8000 },
        );
      }
      const rt = searchParams.get("returnTo");
      navigate(rt || getDefaultRoute(result.user?.role));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    setLoading(true);
    try {
      const userData = await verify2FALogin(tempToken, totpCode);
      toast.success("Welcome back!");
      const rt = searchParams.get("returnTo");
      navigate(rt || getDefaultRoute(userData?.role));
    } catch (err) {
      toast.error(err.message);
      setTotpCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleRecoverySubmit = async (e) => {
    e.preventDefault();
    if (!recoveryCode.trim()) return;
    setLoading(true);
    try {
      const { user: userData, recovery_codes_remaining } = await verify2FARecovery(tempToken, recoveryCode.trim());
      toast.success("Signed in with a recovery code.");
      if (typeof recovery_codes_remaining === "number" && recovery_codes_remaining <= 2) {
        toast.warning(
          `Only ${recovery_codes_remaining} recovery code${recovery_codes_remaining === 1 ? "" : "s"} left. Regenerate them in Security Settings.`,
          { duration: 8000 },
        );
      }
      const rt = searchParams.get("returnTo");
      navigate(rt || getDefaultRoute(userData?.role));
    } catch (err) {
      toast.error(err.message);
      setRecoveryCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTempToken(null);
    setTotpCode("");
    setUseRecovery(false);
    setRecoveryCode("");
  };

  const handleEnrolled = (accessToken, userData) => {
    applyAuth(accessToken, userData);
    toast.success("Two-factor authentication enabled. You're all set!");
    const rt = searchParams.get("returnTo");
    navigate(rt || getDefaultRoute(userData?.role));
  };

  const handleEnrollCancel = () => {
    setEnrollMode(false);
    setEnrollToken(null);
    setFormData({ email: "", password: "" });
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      <BrandPanel brand={brand} />

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-sm">
          {/* Mobile header */}
          <div className="lg:hidden text-center mb-8">
            <div className="relative w-24 h-24 mx-auto mb-4">
              <div className="relative w-24 h-24 rounded-2xl flex items-center justify-center shadow-xl ring-1 ring-slate-200 av-tile bg-white p-2">
                <img src="/avida-logo-mark.png" alt="Avida" className="w-full h-full object-contain" draggable={false} />
              </div>
            </div>
            <h1
              className="text-xl font-black"
              style={{ color: NAVY, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}
            >
              {brand.name}
            </h1>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase mt-0.5" style={{ color: NAVY }}>
              {brand.tagline}
            </div>
            <p className="text-xs text-slate-500 mt-3 px-4 leading-relaxed">{brand.description}</p>
          </div>

          {enrollMode ? (
            <MfaEnrollment
              tempToken={enrollToken}
              email={formData.email}
              onEnrolled={handleEnrolled}
              onCancel={handleEnrollCancel}
              navy={NAVY}
              gold={GOLD_DEEP}
            />
          ) : !requires2FA ? (
            <>
              {searchParams.get("reason") === "security_logout" && (
                <div
                  className="mb-6 flex items-start gap-3 rounded-xl border p-3.5"
                  style={{ background: "#FFF9E6", borderColor: "#F4D06F" }}
                  data-testid="security-logout-banner"
                >
                  <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" style={{ color: GOLD_DEEP }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: NAVY }}>You were signed out for a security update</p>
                    <p className="text-xs text-slate-600 mt-0.5">Everything&apos;s fine — please log in again to continue.</p>
                  </div>
                </div>
              )}
              <div className="mb-8">
                <div
                  className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase mb-2"
                  style={{ color: GOLD_DEEP }}
                >
                  Secure sign-in
                </div>
                <h2
                  className="text-3xl font-black"
                  style={{ color: NAVY, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}
                >
                  Welcome back.
                </h2>
                <p className="text-slate-500 mt-2 text-sm">Sign in to continue to {brand.name}.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-slate-700 text-xs font-bold tracking-wide uppercase">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@avidahealth.org"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl transition-all focus:border-[#FFDA00] focus:ring-[#FFDA00]/30 focus:bg-white"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 text-xs font-bold tracking-wide uppercase">
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-[11px] font-semibold hover:underline"
                      style={{ color: NAVY }}
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-11 h-12 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-xl transition-all focus:border-[#FFDA00] focus:ring-[#FFDA00]/30 focus:bg-white"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid="toggle-password-btn"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-bold text-sm shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                  style={{
                    background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`,
                    color: NAVY,
                  }}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing you in…
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              {/* Caregiver sign-in — separate realm (Staff/PCW isolation) */}
              <div className="mt-5 text-center">
                <Link
                  to="/pcw-login"
                  className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                  data-testid="caregiver-login-link"
                >
                  Caregiver (PCW)? Sign in here →
                </Link>
              </div>

              {/* Mobile module peek */}
              <div className="lg:hidden mt-8">
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase mb-3 text-slate-400">
                  What&apos;s inside
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="login-modules-mobile">
                  {brand.modules.map((m) => (
                    <span
                      key={m.label}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-[10.5px] font-medium text-slate-600"
                    >
                      <m.icon className="w-3 h-3" style={{ color: NAVY }} />
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-md"
                  style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)` }}
                >
                  <ShieldCheck className="w-7 h-7" style={{ color: NAVY }} strokeWidth={2.5} />
                </div>
                <h2
                  className="text-2xl font-black"
                  style={{ color: NAVY, fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}
                >
                  Two-factor required.
                </h2>
                <p className="text-slate-500 mt-1.5 text-sm">
                  {useRecovery
                    ? "Enter one of your one-time backup recovery codes."
                    : "Enter the 6-digit code from your authenticator app."}
                </p>
              </div>

              {useRecovery ? (
                <form onSubmit={handleRecoverySubmit} className="space-y-5" data-testid="recovery-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="recovery" className="text-slate-700 text-xs font-bold tracking-wide uppercase">
                      Recovery code
                    </Label>
                    <Input
                      id="recovery"
                      type="text"
                      autoFocus
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      value={recoveryCode}
                      onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                      className="h-14 text-center text-lg tracking-[0.2em] font-mono bg-slate-50 border-slate-200 text-slate-900 rounded-xl focus:border-[#FFDA00] focus:ring-[#FFDA00]/30 focus:bg-white"
                      required
                      data-testid="recovery-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !recoveryCode.trim()}
                    className="w-full h-12 rounded-xl font-bold text-sm shadow-lg disabled:opacity-60"
                    style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: NAVY }}
                    data-testid="recovery-submit-btn"
                  >
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying…</>) : "Verify & sign in"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setUseRecovery(false); setRecoveryCode(""); }}
                    className="w-full text-xs font-semibold hover:underline"
                    style={{ color: NAVY }}
                    data-testid="use-authenticator-btn"
                  >
                    ← Use my authenticator app instead
                  </button>
                </form>
              ) : (
              <form onSubmit={handle2FASubmit} className="space-y-5" data-testid="totp-form">
                <div className="space-y-1.5">
                  <Label htmlFor="totp" className="text-slate-700 text-xs font-bold tracking-wide uppercase">
                    Verification code
                  </Label>
                  <Input
                    ref={totpInputRef}
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono bg-slate-50 border-slate-200 text-slate-900 rounded-xl focus:border-[#FFDA00] focus:ring-[#FFDA00]/30 focus:bg-white"
                    required
                    data-testid="totp-input"
                  />
                </div>

                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Code refreshes every 30 seconds</span>
                </div>

                <Button
                  type="submit"
                  disabled={loading || totpCode.length !== 6}
                  className="w-full h-12 rounded-xl font-bold text-sm shadow-lg disabled:opacity-60"
                  style={{
                    background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`,
                    color: NAVY,
                  }}
                  data-testid="totp-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    "Verify & sign in"
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="w-full text-xs text-slate-500 hover:text-slate-700"
                  data-testid="back-to-login-btn"
                >
                  ← Back to login
                </button>
                <button
                  type="button"
                  onClick={() => { setUseRecovery(true); setTotpCode(""); }}
                  className="w-full text-xs font-semibold hover:underline"
                  style={{ color: NAVY }}
                  data-testid="use-recovery-code-btn"
                >
                  Lost your device? Use a recovery code
                </button>
              </form>
              )}
            </>
          )}

          <div className="mt-10 pt-6 border-t border-slate-100 text-center">
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Need access? Contact your administrator for an invitation.
            </p>
            <p className="text-[10px] text-slate-300 mt-2 font-mono tracking-wider">
              AVIDA · HIPAA-GRADE
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
